import { SECTION } from "constants/SettingsConstants";
import "./MSAPageContainer.scss";

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { setService, setValidation } from "reducers/future-medical";
import { setCurrentSection, setValidationState } from "reducers/main";
import { useGetMSAValidationByIdQuery } from "services/msa-api";
import type {
  MSADataType,
  MSAValidation,
  ValidationDataType,
} from "types/DataTypes";
import { useToken } from "utils/context";
import { useDispatch } from "utils/redux";

type MSAContainerPropTypes = {
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  layout: "full" | "split"; // full is for a typical table view, split is for a dual form view
  msa?: MSADataType;
};

const MSAPageContainer = ({
  header,
  footer,
  children,
  layout = "full",
  msa,
}: MSAContainerPropTypes) => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { msaId } = useParams<{ msaId?: string }>();

  const { data: msaValidation } = useGetMSAValidationByIdQuery<{
    data: MSAValidation;
  }>({ msaId }, { skip: !msaId || !accessToken });

  useEffect(() => {
    dispatch(setService(msa));
  }, [dispatch, msa]);

  useEffect(() => {
    dispatch(setValidation(msaValidation));
  }, [dispatch, msaValidation]);

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.MSA));
  }, [dispatch]);

  useEffect(() => {
    if (msaValidation) {
      // Extract and separate errors and warnings from the single `errors` array
      const extractedErrors: ValidationDataType[] = msaValidation.errors.filter(
        (error: ValidationDataType) => error.level === "ERROR",
      );

      const extractedWarnings: ValidationDataType[] =
        msaValidation.errors.filter(
          (warning: ValidationDataType) => warning.level === "WARN",
        );

      dispatch(
        setValidationState({
          errors: extractedErrors,
          warnings: extractedWarnings,
        }),
      );
    }
  }, [dispatch, msaValidation]);

  return (
    <div className="swim-wrapper h-100">
      {header && <div className="MSAPageContainer__Header">{header}</div>}
      <div
        className={`grid-layout gl-2 no-gap-tb swimlanes msa-medical-${layout}`}
      >
        {children}
        {footer && <div className="MSAPageContainer__Footer">{footer}</div>}
      </div>
    </div>
  );
};

export default MSAPageContainer;
